import React from 'react';
import ReportDesktop from './desktop/View';
import ReportMobile from './mobile/View';
import useWindowWidth from 'components/hooks/useWindowWidth';
import useReport from './hooks/useReport';
import useMeStore from 'store/useMeStore';
import { SETTINGS } from 'typings/user';
import Report from 'report/web/Report';
import { ReportDraftProvider } from 'report/web/ReportDraftProvider';
import { useAssignmentInformation } from '../page/hooks/useAssignment';
import { AssignmentStatus } from 'typings/assignment';

interface Props {
    assignmentID: string;
}

export default function View({ assignmentID }: Props): JSX.Element {
    const me = useMeStore(state => state.me);
    const token = useMeStore(state => state.token);
    const { isMobile } = useWindowWidth();
    const formID = React.useId();
    const reportProps = useReport(assignmentID);
    const useSplitTextfields =
        !!me?.userSettings?.[SETTINGS.USE_SPLIT_TEXTFIELDS_TIMEREPORT]?.value;
    const status = reportProps.assignmentInfo?.status;
    const canSubmit =
        status === AssignmentStatus.BOOKED ||
        status === AssignmentStatus.TIME_REPORTED;
    const { fetch } = useAssignmentInformation({ id: assignmentID, token });

    // Refresh the assignment row's status after a successful submission.
    React.useEffect(() => {
        if (reportProps.success) {
            fetch();
        }
    }, [reportProps.success]);

    const Layout = isMobile ? ReportMobile : ReportDesktop;

    return (
        <ReportDraftProvider
            key={assignmentID}
            report={reportProps.report}
            useSplitTextfields={useSplitTextfields}>
            <Layout
                assignmentID={assignmentID}
                assignment={reportProps.assignmentInfo}
                year={reportProps.assignmentInfo?.year || null}
                week={reportProps.assignmentInfo?.week || null}
                loading={reportProps.loading}
                submitting={reportProps.submitting}
                success={reportProps.success}
                canSubmit={canSubmit && !!reportProps.report}
                formID={formID}>
                <Report
                    type={reportProps.report?.template.type}
                    formID={formID}
                    readOnly={!canSubmit}
                    disabled={reportProps.loading || reportProps.submitting}
                    loading={reportProps.loading}
                    success={reportProps.success}
                    error={reportProps.error}
                    isMobile={isMobile}
                    onSubmit={reportProps.submit}
                />
            </Layout>
        </ReportDraftProvider>
    );
}
